import { useCheckAccountTraits } from '@cmg/auth';
import { countriesList } from '@cmg/common';
import defaultTo from 'lodash/defaultTo';

import { useGetOfferingCountryCode } from '../../../../../../../../common/hooks/useGetOfferingCountryCode';
import { isValidCmgEntityKey } from '../../../../../../../../common/util/isValidCmgEntityKey';
import { isInvestorUnlinked } from '../../../../../../../../common/util/rolodex/unlinked-investor.util';
import {
  AttestationFormStatus,
  CertificateType,
  OfferingType,
} from '../../../../../../../../graphql';
import offeringSidePanelSellSideRouteFactory from '../../../../../../../offering-side-panel/offeringSidePanelSellSideRouteFactory';
import { OrderBookPersonaType } from '../../../../../hooks/useIdentifyOrderBookPersona';
import { useCoveredAttestationQuery } from '../../sale-and-trading/useCoveredAttestationQuery';
import { QIB_COMPLIANCE_WARNING_MESSAGE } from '../syndicate/useCertificatesSyndicateModel';
import { CertificateModel, PersonaProps } from '../useCertificatesModel';

export type Props = PersonaProps & {
  offeringId: string;
  cmgEntityKey: string | undefined;
  certificateType: CertificateType | null;
  isReg144A: boolean;
  enforceValidAttestationForSalesAndTrading?: boolean;
  // The parent IndicationActivitySalesAndTradingContextProvider already knows the offering's type
  // from its own indication query - pass it through instead of re-fetching it here. Re-fetching
  // used to mean running the same shared SalesAndTradingIndication query a second time with
  // indicationId: null (needed only for the unlinked-investor case, when the attestation query
  // below is skipped), and that second response would normalize into the same Apollo cache entity
  // as the parent's, racing it and intermittently clobbering fields like duplicateIndicationIds.
  offeringType: OfferingType | undefined;
};

export const COMPLIANCE_WARNING_MESSAGE_SALE_AND_TRADING =
  'To submit, modify, and acknowledge indications, review the outstanding requirements in the Compliance section.';

export const useSalesAndTradingCertificatesModel = ({
  type,
  cmgEntityKey,
  offeringId,
  certificateType,
  isReg144A,
  enforceValidAttestationForSalesAndTrading,
  offeringType: offeringTypeFromParent,
}: Props): CertificateModel => {
  const isSaleTrading = type === OrderBookPersonaType.SALES_AND_TRADING;
  const hasCertificateLibraryTrait = useCheckAccountTraits(['CERTIFICATE_LIBRARY']);
  const offeringCountryCode = useGetOfferingCountryCode(offeringId);
  const isUsOffering = offeringCountryCode === countriesList.Usa.countryCode;

  const coveredAttestationQuery = useCoveredAttestationQuery(
    {
      cmgEntityKey: cmgEntityKey!,
      offeringId,
      certificateType: certificateType!,
    },
    {
      skip:
        !isSaleTrading ||
        !isValidCmgEntityKey(cmgEntityKey) ||
        !offeringId ||
        !certificateType ||
        !hasCertificateLibraryTrait,
    }
  );

  const { coveredAttestation = null, basicPublishedOffering = null } =
    coveredAttestationQuery.data ?? {};

  let indicationAttestation = defaultTo(coveredAttestationQuery.data?.indicationAttestation, null);

  if (!isUsOffering && indicationAttestation?.certificateType === CertificateType.Form51305131) {
    indicationAttestation = null;
  }

  // basicPublishedOffering.type covers the normal case; offeringTypeFromParent is the fallback for
  // when coveredAttestationQuery is skipped (e.g. an unlinked investor), and comes from the
  // parent's own indication query rather than a redundant fetch of this hook's own.
  const offeringType = basicPublishedOffering?.type ?? offeringTypeFromParent;

  const hasComplianceWarning =
    (indicationAttestation?.status === AttestationFormStatus.Expired ||
      indicationAttestation?.status === AttestationFormStatus.NotOnFile ||
      (isInvestorUnlinked(cmgEntityKey) ?? false)) &&
    (offeringType === OfferingType.Ipo || certificateType === CertificateType.Qib);

  const redirectUrl = cmgEntityKey
    ? offeringSidePanelSellSideRouteFactory.orderBookCompliance.getUrlPath({
        cmgEntityKey: cmgEntityKey,
      })
    : null;

  const canByPassComplianceWarning =
    enforceValidAttestationForSalesAndTrading === false &&
    (isReg144A || offeringType === OfferingType.Ipo);

  const isQibComplianceWarningWithBypass =
    certificateType === CertificateType.Qib && canByPassComplianceWarning;

  return {
    canRead: !!cmgEntityKey && isSaleTrading,
    attestation: indicationAttestation,
    attestationForm: coveredAttestation?.attestationForm ?? null,
    loading: coveredAttestationQuery.loading,
    offeringType: offeringType,
    certificateType,
    warning: {
      hasComplianceWarning,
      canByPassComplianceWarning,
      message: isQibComplianceWarningWithBypass
        ? QIB_COMPLIANCE_WARNING_MESSAGE
        : COMPLIANCE_WARNING_MESSAGE_SALE_AND_TRADING,
      redirectUrl,
      tooltipMessage: null,
    },
  };
};
